import { getFirstNestJsGraphQLError } from '@pcg/graphql-kit';
import { storeToRefs } from 'pinia';
import { readonly } from 'vue';

import { useErrorStore } from '@/shared/stores/error';

import type { ErrorLike } from '@apollo/client';
import type { ShowErrorOptions } from '@/shared/stores/types';

interface ErrorConfig {
  title: string;
  description: string;
}

interface ToastErrorOptions {
  /** Override the toast title. Defaults to the generic internal-error title. */
  title?: string;
}

const INTERNAL_ERROR: ErrorConfig = {
  title: 'Внутренняя ошибка',
  description: 'Что-то пошло не так. Попробуй позже.',
};

const ERROR_CONFIG: Record<number, ErrorConfig> = {
  401: {
    title: 'Нужен вход',
    description: 'Войди, чтобы продолжить.',
  },
  403: {
    title: 'Нет доступа',
    description: 'У тебя нет прав на этот раздел.',
  },
  404: {
    title: 'Страница не найдена',
    description: 'По этому адресу ничего нет.',
  },
  500: INTERNAL_ERROR,
};

/** Status codes that get a full-page UErrorView rather than a toast. */
const ERROR_VIEW_STATUS_CODES = [401, 403, 404, 500];

const getErrorConfig = (statusCode: number): ErrorConfig => ERROR_CONFIG[statusCode] ?? INTERNAL_ERROR;

const isErrorViewStatusCode = (statusCode: number): boolean => ERROR_VIEW_STATUS_CODES.includes(statusCode);

/**
 * Distinguish an Apollo error (passed by an `onError` hook) from a
 * `ToastErrorOptions` config object — every `ErrorLike` carries a `message`.
 */
const isErrorLike = (value: unknown): value is ErrorLike => {
  return typeof value === 'object' && value !== null && 'message' in value;
};

/**
 * Error handling with the app's Russian copy.
 *
 * @example
 * ```ts
 * const { error, showError, clearError, handleServerErrorWithToast } = useError();
 *
 * showError({ statusCode: 404 });
 *
 * const { onError } = useMutation(SomeDocument);
 * onError(handleServerErrorWithToast({ title: 'Не удалось сохранить' }));
 * ```
 */
export const useError = () => {
  const toast = useToast();
  const errorStore = useErrorStore();
  const { error } = storeToRefs(errorStore);

  const showError = (options: ShowErrorOptions = {}) => {
    const statusCode = options.statusCode ?? 500;
    const config = getErrorConfig(statusCode);

    errorStore.setError({
      statusCode,
      title: options.title ?? config.title,
      description: options.description ?? config.description,
    });
  };

  const clearError = () => {
    errorStore.clearError();
  };

  /**
   * Show a full-page UErrorView for 401/403/404/500. Anything else is ignored —
   * use `handleServerErrorWithToast` when a toast is the right surface.
   */
  const handleServerError = (serverError: ErrorLike) => {
    const nestJsError = getFirstNestJsGraphQLError(serverError);

    if (nestJsError && isErrorViewStatusCode(nestJsError.extensions.statusCode)) {
      showError({
        statusCode: nestJsError.extensions.statusCode,
        description: nestJsError.message,
      });
    }
  };

  const showErrorToast = (serverError: ErrorLike, options?: ToastErrorOptions) => {
    console.error('Server error:', serverError);
    const nestJsError = getFirstNestJsGraphQLError(serverError);
    const description = nestJsError?.message ?? serverError.message ?? INTERNAL_ERROR.description;

    toast.add({
      title: options?.title ?? INTERNAL_ERROR.title,
      description,
      color: 'error',
    });
  };

  /**
   * Toast handler for Apollo `onError`. Works two ways:
   *
   * @example Pass the handler directly (default title):
   * ```ts
   * onError(handleServerErrorWithToast);
   * ```
   *
   * @example Curry with a custom title:
   * ```ts
   * onError(handleServerErrorWithToast({ title: 'Не удалось сохранить прогресс' }));
   * ```
   */
  function handleServerErrorWithToast(serverError: ErrorLike): void;
  function handleServerErrorWithToast(options?: ToastErrorOptions): (serverError: ErrorLike) => void;
  function handleServerErrorWithToast(
    arg?: ErrorLike | ToastErrorOptions,
  ): void | ((serverError: ErrorLike) => void) {
    if (isErrorLike(arg)) {
      showErrorToast(arg);

      return;
    }

    const options = arg;

    return (serverError: ErrorLike) => showErrorToast(serverError, options);
  }

  return {
    /** Current error state (readonly reactive ref) */
    error: readonly(error),
    showError,
    clearError,
    /** Apollo handler — full-page error view for 401/403/404/500 */
    handleServerError,
    /** Apollo handler — toast */
    handleServerErrorWithToast,
  };
};
